import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '~components/ui/Button'
import { authenticate } from '~redux/auth'
import Sprite from '~components/Sprite'
import Link from '../../ui/Link'
import RoundedIcon from '~components/ui/RoundedIcon'
import Message from '~components/ui/Message'
import { pageView } from '~helpers/ga'
import { flattenDeep } from 'lodash/array'

const ResetPassword = ({ isAuthenticated, authenticate }) => {
  const navigate = useNavigate()
  const params = useParams()
  const copyrightDate = new Date().getFullYear()

  const [formData, setFormData] = useState({
    busy: false,
    complete: false,
    bottomLinks: [],
    messageText: '',
    messageType: '',
    email: decodeURIComponent(params.email),
    token: decodeURIComponent(params.token),
    password: '',
    password_confirmation: ''
  })

  useEffect(() => {
    pageView()

    axios
      .get('/options', {
        params: {
          option: 'footer'
        }
      })
      .then(response => {
        setFormData(prevState => ({
          ...prevState,
          bottomLinks: response.data.fields.bottom_links
        }))
      })

    document.body.classList.add('login-body')
  }, [])

  const setMessage = (type, message) => {
    setFormData(prevState => ({
      ...prevState,
      messageType: type,
      messageText: message
    }))

    setTimeout(() => {
      setFormData(prevState => ({
        ...prevState,
        messageType: ''
      }))
    }, 5000)
  }

  const handleInputChange = event => {
    const target = event.target
    const name = target.name
    let value = target.value

    if (target.type === 'checkbox') {
      value = target.checked
    }

    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }))
  }

  const handleSubmit = () => {
    if (formData.busy) {
      return
    }

    const password = formData.password
    const passwordConfirmation = formData.password_confirmation

    if (password !== passwordConfirmation) {
      setMessage('error', 'Your passwords do not match')
    } else {
      setFormData(prevState => ({
        ...prevState,
        busy: true
      }))

      axios
        .post('/reset-password', formData)
        .then(response => {
          setFormData(prevState => ({
            ...prevState,
            complete: true
          }))
        })
        .catch(e => {
          if (e.response.status === 422) {
            const error = flattenDeep(Object.values(e.response.data.errors))
            setMessage('error', error)
            return
          }
          setMessage('error', 'An unknown error occurred')
        })
        .finally(() => {
          setFormData(prevState => ({
            ...prevState,
            busy: false
          }))
        })
    }
  }

  if (isAuthenticated) {
    navigate('/')
    return null
  }

  if (formData.complete) {
    setMessage('success', 'Your password has been updated, please login below')
    navigate('/account/login')
    return null
  }

  return (
    <>
      {formData.messageText && (
        <Message
          messageType={formData.messageType}
          messageText={formData.messageText}
        />
      )}
      <div className="mx-auto xxl:container md:px-8 xxl:px-0 login-container">
        <div className="w-full px-4 py-8 mx-auto md:p-10 lg:w-2/3 xl:w-1/2 xl:p-20 bg-lightGrey">
          <a
            href="/"
            className="z-40 block w-17 h-10 md:w-22.5 md:h-13.5 transition-opacity duration-150 hover:opacity-75 focus:opacity-75"
          >
            <Sprite name="logo" />
          </a>

          <h2 className="text-left mt-15 text-md">
            Reset your password
          </h2>

          <form
            className="mt-4"
            action="#"
            onSubmit={e => e.preventDefault() || handleSubmit()}
            autoComplete="off"
          >
            <div className="w-full mt-3 lg:w-2/3 xl:w-3/4">
              <label className="inline-block mt-4">
                Email
              </label>
              <input
                aria-label="Email address"
                name="email"
                type="email"
                required
                readOnly
                value={formData.email}
                className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10"
                placeholder="Email"
                autoComplete="off"
              />
            </div>

            <div className="w-full mt-3 lg:w-2/3 xl:w-3/4">
              <label className="inline-block mt-4">
                New password
              </label>
              <input
                aria-label="Password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10"
                placeholder="Password"
                autoComplete="off"
              />
            </div>

            <div className="w-full mt-3 lg:w-2/3 xl:w-3/4">
              <label className="inline-block mt-4">
                Confirm password
              </label>
              <input
                aria-label="Confirm password"
                name="password_confirmation"
                type="password"
                required
                value={formData.password_confirmation}
                onChange={handleInputChange}
                className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10"
                placeholder="Confirm password"
                autoComplete="off"
              />
            </div>

            <div className="mt-6">
              <Button type="submit" className="w-auto mt-4" disabled={formData.busy}>
                Reset password
              </Button>
            </div>
          </form>
        </div>

        <div className="flex flex-wrap w-full mx-auto mt-10 lg:w-2/3 xl:w-1/2 md:flex-col-reverse md:border-coral md:border-t md:pt-4 lg:flex-row-reverse lg:flex-no-wrap lg:items-start">
          <a
            href="mailto:info@panache-lingerie.com"
            className="inline-flex items-center flex-grow-0 flex-shrink-0 mx-auto mb-10 group md:mt-4 md:mx-0 md:mb-0 lg:mt-0 lg:ml-4"
          >
            <RoundedIcon name="label" className="inline-block mr-2 w-7 h-7" />
            Need help?
          </a>

          <ul className="flex-wrap w-full -mb-3 text-center md:w-auto md:inline-flex md:text-left md:-mb-1 lg:mr-auto">
            <li className="hidden mb-3 md:mb-1 md:mr-3 md:block">
              &copy; Panache
              {' '}
              {copyrightDate}
            </li>
            {formData.bottomLinks.map(link => (
              <li key={link.key} className="block mb-3 md:mb-1 md:mr-3">
                <Link href={link.attributes.link} className="hover:underline">
                  {link.attributes.label}
                </Link>
              </li>
            ))}
            <li className="block mb-3 md:mb-1 md:mr-3 md:hidden">
              &copy; Panache
              {' '}
              {copyrightDate}
            </li>
          </ul>
        </div>
      </div>
    </>
  )
}

ResetPassword.propTypes = {
  isAuthenticated: PropTypes.bool,
  authenticate: PropTypes.func
}

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated
})

const mapDispatchToProps = { authenticate }

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword)
